import { Button } from 'antd';
import { BarChart3, ClipboardCheck, Download, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { sectionTrail } from '../../../shared/components/section-trail';
import { buildRateCardColumns } from '../components/build-rate-card-columns';
import { CreateRateCardDrawer } from '../components/CreateRateCardDrawer';
import { rateLineTemplateUrl } from '../rate-cards.api';
import { RATE_CARD_APPROVALS_PATH, RATE_CARD_OFFCARD_REPORT_PATH } from '../rate-cards.paths';
import { RateCardsTable } from '../components/RateCardsTable';
import { useRateCardList } from '../hooks/use-rate-card-list';
import { useRateCardPermissions } from '../hooks/use-rate-card-permissions';

export function RateCardsPage() {
  const { state, list, isRefreshing, reload } = useRateCardList();
  const { canEdit, canApprove } = useRateCardPermissions();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  const columns = buildRateCardColumns({ list, canEdit, onDeactivated: reload });

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('rate-cards')}
        title="Rate cards"
        description="What each customer is charged per lane, and the standard card spot business falls back to."
        actions={
          <>
            {/* The pricing desk's view; the report itself needs `job.view` and sends anyone without it back here. */}
            {canApprove && (
              <Link to={RATE_CARD_OFFCARD_REPORT_PATH}>
                <Button icon={<BarChart3 className="size-4" />}>Off-card report</Button>
              </Link>
            )}
            <a href={rateLineTemplateUrl()}>
              <Button icon={<Download className="size-4" />}>Blank template</Button>
            </a>
            {canApprove && (
              <Link to={RATE_CARD_APPROVALS_PATH}>
                <Button icon={<ClipboardCheck className="size-4" />}>Approval queue</Button>
              </Link>
            )}
            {canEdit && (
              <Button type="primary" icon={<Plus className="size-4" />} onClick={() => setDrawerOpen(true)}>
                Create rate card
              </Button>
            )}
          </>
        }
      />
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load rate cards" description="Something went wrong loading the rate cards. Try reloading the page." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading rate cards" />}
      {state.status === 'loaded' && <RateCardsTable page={state.page} columns={columns} list={list} isRefreshing={isRefreshing} />}
      <CreateRateCardDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onCreated={reload} />
    </>
  );
}
